import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { CHIP_COLOUR_CLASSES, type ChipColour } from "./chip-colours"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Status tones (soft background). For a status value, prefer StatusBadge.
        success: "border-success-border bg-success-soft text-success-strong",
        warning: "border-warning-border bg-warning-soft text-warning-strong",
        info: "border-info-border bg-info-soft text-info-strong",
        danger: "border-danger-border bg-danger-soft text-danger-strong",
        muted: "bg-muted text-muted-foreground",
        ai: "border-ai-border bg-ai-soft text-ai-strong",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
      size: {
        default: "",
        // Inline chips in dense rows and table cells.
        sm: "px-1.5 py-0 text-2xs [&>svg]:size-2.5",
      },
      shape: {
        pill: "rounded-full",
        // A tag or label chip (task labels, Gmail labels, the Inbox's facts).
        square: "rounded",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "pill",
    },
  }
)

/**
 * A small label. `colour` gives it a categorical colour from the one palette (chip-colours.ts)
 * instead of a variant; `onRemove` adds an X button (give `removeLabel` when the children aren't
 * plain text).
 */
function Badge({
  className,
  variant = "default",
  size = "default",
  shape = "pill",
  colour,
  onRemove,
  removeLabel,
  removeDisabled = false,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** A categorical colour (a label's, or colourForName's); replaces the variant. */
    colour?: ChipColour
    /** Shows an X after the label that calls this. Not with asChild. */
    onRemove?: () => void
    /** The X's accessible name; defaults to "Remove <children>" when the children are text. */
    removeLabel?: string
    removeDisabled?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "span"
  const removable = Boolean(onRemove) && !asChild
  const xLabel =
    removeLabel ??
    (typeof children === "string" || typeof children === "number"
      ? `Remove ${children}`
      : "Remove")

  return (
    <Comp
      data-slot="badge"
      data-variant={colour ? "colour" : variant}
      data-size={size}
      className={cn(
        badgeVariants({ variant: colour ? null : variant, size, shape }),
        colour && CHIP_COLOUR_CLASSES[colour],
        removable && (size === "sm" ? "gap-0.5 pr-0.5" : "pr-1"),
        className
      )}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {children}
          {removable ? (
            <button
              type="button"
              aria-label={xLabel}
              disabled={removeDisabled}
              onClick={(e) => {
                e.stopPropagation()
                onRemove?.()
              }}
              className={cn(
                "inline-flex shrink-0 items-center justify-center opacity-70 outline-none hover:bg-foreground/10 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-30",
                shape === "square" ? "rounded-sm" : "rounded-full",
                size === "sm" ? "p-px" : "p-0.5"
              )}
            >
              <XIcon className={size === "sm" ? "size-2.5" : "size-3"} />
            </button>
          ) : null}
        </>
      )}
    </Comp>
  )
}

export { Badge, badgeVariants }
